import { Fragment, useState } from 'react';
import setDicomRecordApi, { DicomRecord } from '../api/setDicomRecord';
import { captureError } from './captureError';
import { track } from '../tracking';
import { DatePicker, Input, Modal, notification } from 'antd';
import moment from 'moment';

interface Props {
    isVisible: boolean;
    toggleLinkDicomModal: () => void;
    p_oid: string;
    refreshDicomRecords: () => void;
}

const dicomRecordUIData: {
    key: keyof DicomRecord;
    label: string;
    type: string;
    required?: boolean;
}[] = [
    { key: 'dicom_p_id', label: 'Dicom P Id', type: 'string', required: true },
    { key: 'study_date', label: 'Study Date', type: 'date' },
    { key: 'first_name', label: 'First Name', type: 'string' },
    { key: 'last_name', label: 'Last Name', type: 'string' },
    { key: 'dob', label: 'Date of Birth', type: 'date' },
    { key: 'accession_number', label: 'Accession Number', type: 'string' },
    { key: 'description', label: 'Description', type: 'string' },
    { key: 'modality', label: 'Modality', type: 'string' },
    { key: 'referring_physician', label: 'Referring Physician', type: 'string' },
    { key: 'instances', label: 'Instances', type: 'string' },
];

const initialDicomRecord: DicomRecord = {
    dicom_p_id: '',
    first_name: '',
    last_name: '',
    dob: '',
    accession_number: '',
    study_date: '',
    description: '',
    modality: '',
    referring_physician: '',
    instances: '',
};

const LinkDicomModal: React.FC<Props> = ({
    isVisible,
    toggleLinkDicomModal,
    p_oid,
    refreshDicomRecords,
}) => {
    const [dicomRecord, setDicomRecord] = useState<DicomRecord>(initialDicomRecord);
    const [loading, setLoading] = useState<boolean>(false);

    async function submitHandler() {
        const requiredDicomRecordKeys = dicomRecordUIData.filter((i) => i.required);
        const emptyRequiredDicomRecordKeys = requiredDicomRecordKeys.filter(
            (i) => !dicomRecord[i.key],
        );
        const emptyRequiredDicomRecordLabels = emptyRequiredDicomRecordKeys.map((i) => i.label);
        if (emptyRequiredDicomRecordLabels.length) {
            notification.error({
                message: `${emptyRequiredDicomRecordLabels.join(', ')} is/are required`,
                duration: 5,
            });
            return;
        }
        setLoading(true);
        try {
            const res = await setDicomRecordApi(p_oid, dicomRecord);
            console.log(res);
            notification.success({
                message: 'Dicom record saved successfully',
                duration: 5,
            });
            toggleLinkDicomModal();
            refreshDicomRecords();
            track('DICOM_RECORD_SAVED', { p_oid });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'setDicomRecord',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error in saving dicom record',
                duration: 5,
            });
        } finally {
            setLoading(false);
        }
    }

    const onChange = (key: string, value: string) => {
        setDicomRecord({
            ...dicomRecord,
            [key]: value,
        });
    };

    return (
        <Modal
            onCancel={toggleLinkDicomModal}
            width={420}
            closable={false}
            visible={isVisible}
            destroyOnClose
            footer={null}
            bodyStyle={{
                padding: '0px',
                height: '540px',
                display: 'flex',
                flexDirection: 'column',
            }}
            className="overflow-hidden rounded-8"
            style={{ height: '540px' }}
        >
            <div className="w-full h-full flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-16 py-14">
                    <p className="text-20">Store Dicom Record(s)</p>
                    <p className="cursor-pointer" onClick={toggleLinkDicomModal}>
                        <svg
                            className="w-14 h-14"
                            viewBox="0 0 12 12"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10.7812 2.28125L7.03125 6.03125L10.75 9.75C11.0625 10.0312 11.0625 10.5 10.75 10.7812C10.4688 11.0938 10 11.0938 9.71875 10.7812L5.96875 7.0625L2.25 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 2 0.90625 1.53125 1.21875 1.21875C1.5 0.9375 1.96875 0.9375 2.28125 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.7812 1.25C11.0625 1.53125 11.0625 2 10.7812 2.28125Z"
                                fill="#1A1A1A"
                            />
                        </svg>
                    </p>
                </div>
                <div className="flex-1 overflow-scroll border-t border-b bg-darwin-neutrals-50 px-16 pb-16 border-darwin-neutral_new-200 pt-16">
                    <div className="grid grid-cols-2 gap-y-8">
                        {dicomRecordUIData.map((item) => {
                            return (
                                <Fragment>
                                    <p>
                                        {item.label}{' '}
                                        {item.required && (
                                            <span className="text-darwin-red-primary text-16">
                                                *
                                            </span>
                                        )}{' '}
                                        :
                                    </p>
                                    {item.type === 'date' ? (
                                        <DatePicker
                                            value={
                                                dicomRecord[item.key]
                                                    ? moment(dicomRecord[item.key])
                                                    : null
                                            }
                                            onChange={(date) =>
                                                onChange(item.key, date?.format('YYYY-MM-DD') || '')
                                            }
                                            allowClear={false}
                                            format="DD/MM/YYYY"
                                        />
                                    ) : (
                                        <Input
                                            value={dicomRecord[item.key]}
                                            onChange={(e) => onChange(item.key, e.target.value)}
                                        />
                                    )}
                                </Fragment>
                            );
                        })}
                    </div>
                </div>
                <div className="p-12 flex items-center justify-between text-14 font-700">
                    <button
                        className="px-12 py-8 border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 rounded-8"
                        onClick={toggleLinkDicomModal}
                    >
                        Close
                    </button>
                    <button
                        className="px-12 py-10 bg-darwin-accent-bright-blue-800 text-white rounded-8 flex items-center space-x-8 disabled:opacity-70 disabled:cursor-not-allowed"
                        onClick={submitHandler}
                        disabled={loading}
                    >
                        {loading && (
                            <p>
                                <svg
                                    className="w-16 h-16 fill-white animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </p>
                        )}
                        <p>Submit</p>
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default LinkDicomModal;
